import React from 'react';
import Breadcrumbs from 'components/breadcrumbs';
import Link from 'components/link';
import Button from 'components/control/button';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { testIds } from 'constants/test-constants';
import { sanitizeTestid } from 'utils/stringSanitization';
import { SITEMAP_FILTERS, isTranslatableFilter } from './constants';
import { LOCALE_COMMON_PATHS } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';
import { translateProvinceName } from 'utils/translate-province';
import { BreadcrumbType } from 'components/breadcrumbs/types';
import type { Breadcrumb } from 'components/breadcrumbs/types';

export type SitemapPlace = {
  name: string;
  link: string;
  label: number;
};

interface Props {
  provinces: SitemapPlace[];
  cities: SitemapPlace[];
  filter?: string;
  country?: string;
  isCountrySpecificSitemap: boolean;
}





export default function Sitemap({ provinces, cities, filter, country = 'ca', isCountrySpecificSitemap = false }: Props) {
  const { t } = useTranslation(Namespaces.COMMON);
  const activeFilter = SITEMAP_FILTERS[SITEMAP_FILTERS.indexOf(filter || 'popular')];
  
  const breadcrumbs: readonly Breadcrumb[] = [
    {
      name: t(LOCALE_COMMON_PATHS.BREADCRUMBS_HOME, { defaultValue: 'Home' }),
      link: '/',
      type: BreadcrumbType.BREADCRUMB_TYPE_STATIC,
    },
    {
      name: t(LOCALE_COMMON_PATHS.BREADCRUMBS_SITEMAP, { defaultValue: 'Sitemap' }),
      link: '/sitemap',
      type: BreadcrumbType.BREADCRUMB_TYPE_STATIC,
    },
  ];
  const LinkToOtherSitemap = () => {
    return (
      <div className={styles['link-to-sitemap']}>
        <span>{t(LOCALE_COMMON_PATHS.SITEMAP_OTHER_SITEMAP_PROMPT, { context: country, defaultValue: 'Looking for {{country}} cities? ' })}
          <Link href={`/sitemap/${country === 'us' ? 'ca' : 'us'}`}>
            <span className={styles['highlighted-link']}>{t(LOCALE_COMMON_PATHS.SITEMAP_LINK_TO_OTHER_SITEMAP, { context: country, defaultValue: 'View {{country}} sitemap' })}</span>
          </Link>
        </span>
      </div>
    );
  };

  const filterCities = () => {
    if (activeFilter === 'popular') {
      return cities?.sort((a, b) => a.label < b.label ? 1 : -1).slice(0, 32).map((city, index) => (
        <p key={index} data-testid={sanitizeTestid(city.name) + testIds.sitemapCity}><Link href={city.link}>{t(LOCALE_COMMON_PATHS.SEO_LINKS_HOMES_FOR_SALE, { place: city.name, defaultValue: '{{place}} Homes for Sale' })}</Link> <span>{city.label}</span></p>
      ));
    } else {
      return cities?.filter(city => city.name.toLowerCase().startsWith(activeFilter)).map((city, index) => (
        <p key={index} data-testid={sanitizeTestid(city.name) + testIds.sitemapCity}><Link href={city.link}>{t(LOCALE_COMMON_PATHS.SEO_LINKS_HOMES_FOR_SALE, { place: city.name, defaultValue: '{{place}} Homes for Sale' })}</Link> <span>{city.label}</span></p>
      ));
    }
  };

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className={styles.component}>
        <h1>{t(LOCALE_COMMON_PATHS.SITEMAP_TITLE, { context: country, defaultValue: 'Real Estate Buy & Sell' })}</h1>
        <p className={styles['subtext']}>
          {t(LOCALE_COMMON_PATHS.SITEMAP_DESCRIPTION, { 
            context: country, 
            defaultValue: 'Find homes and condos for sale or sold prices by cities.', 
          })}
        </p>
        {!isCountrySpecificSitemap && <LinkToOtherSitemap />}
        <section>
          <h2>{t(LOCALE_COMMON_PATHS.SITEMAP_CITIES, { context: country, defaultValue: 'Cities' })}</h2>
          <div className={styles.filters}>
            {SITEMAP_FILTERS.map((filter, index) => (
              <Link key={index} href={`/sitemap/${country}/${filter}`} as={`/sitemap/${country}/${filter}`}>
                <Button
                  label={isTranslatableFilter(filter) ? t(LOCALE_COMMON_PATHS.SITEMAP_POPULAR, { defaultValue: filter }) : filter}
                  theme="secondary"
                  className={buildClassName(styles.filter, filter === activeFilter && styles.active)}
                />
              </Link>
            ))}
          </div>
          <div className={styles.list}>{filterCities()}</div>
        </section>
        <section>
          <h2>{t(LOCALE_COMMON_PATHS.SITEMAP_PROVINCES, { context: country, defaultValue: 'Provinces' })}</h2>
          <div className={styles.list}>
            {provinces.map((province, index) => (
              <p key={index}><Link href={province.link} testId={sanitizeTestid(province.name) + testIds.sitemapProvince}>{translateProvinceName(province.name, t)}</Link> <span>{province.label}</span></p>
            ))}
          </div>
        </section>
        <p className={styles.disclaimer}>{t(LOCALE_COMMON_PATHS.SITEMAP_DISCLAIMER, { defaultValue: 'Disclaimer: Listing count is for reference use only and may differ from actual numbers due to rapid market changes.' })}</p>
      </div>
    </>
  );
}
